import { API_URL } from '../config';

const AUTH_BASE_URL = API_URL.replace('/api', '');
const TOKEN_KEY = 'auth_token';

/**
 * SECURITY: Token storage with HttpOnly cookie support
 * localStorage is used as fallback for backward compatibility
 * Primary auth is via HttpOnly cookie set by backend
 */

/**
 * Get auth token from localStorage (fallback only)
 * SECURITY: Primary auth is via HttpOnly cookies
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Set auth token in localStorage (fallback only)
 * SECURITY: Token is also set in HttpOnly cookie by backend
 */
export const setToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * Remove auth token from localStorage
 */
export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Get authorization headers
 * SECURITY: Includes token for backward compatibility with API clients
 */
export const getAuthHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Fetch CSRF token from backend
 * SECURITY: Required for state-changing operations
 */
let csrfToken = null;

export const fetchCsrfToken = async () => {
  try {
    const response = await fetch(`${AUTH_BASE_URL}/auth/csrf-token`, {
      method: 'GET',
      credentials: 'include', // SECURITY: Include cookies
    });
    const data = await response.json();
    if (data.csrfToken) {
      csrfToken = data.csrfToken;
    }
    return csrfToken;
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    return null;
  }
};

/**
 * Get current CSRF token
 */
export const getCsrfToken = () => csrfToken;

/**
 * Get current authenticated user
 * SECURITY: Uses HttpOnly cookie for authentication
 * @returns {Promise<Object|null>} User object or null
 */
export const getCurrentUser = async () => {
  try {
    // SECURITY: Use /auth/status which reads HttpOnly cookie
    const response = await fetch(`${AUTH_BASE_URL}/auth/status`, {
      method: 'GET',
      credentials: 'include', // SECURITY: Include HttpOnly cookies
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders() // Fallback header
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        removeToken();
        return null;
      }
      throw new Error('Failed to fetch user');
    }

    const data = await response.json();
    
    if (data.authenticated && data.user) {
      // Fetch CSRF token for subsequent requests
      await fetchCsrfToken();
      return data.user;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching current user:', error);
    return null;
  }
};

/**
 * Logout user
 * SECURITY: Calls backend to clear HttpOnly cookie and blacklist token
 * @returns {Promise<boolean>} Success status
 */
export const logout = async () => {
  try {
    // SECURITY: Call backend to clear HttpOnly cookie
    await fetch(`${AUTH_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include', // SECURITY: Include cookies
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        ...getAuthHeaders()
      }
    });
    
    removeToken();
    csrfToken = null;
    return true;
  } catch (error) {
    console.error('Error logging out:', error);
    removeToken();
    return false;
  }
};

/**
 * Get Google OAuth URL
 * @returns {string} Google OAuth URL
 */
export const getGoogleAuthUrl = () => {
  return `${AUTH_BASE_URL}/auth/google`;
};

/**
 * Get headers for authenticated API requests
 * SECURITY: Includes CSRF token for state-changing operations
 */
export const getSecureHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders()
  };
  
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }
  
  return headers;
};

const authService = {
  getCurrentUser,
  logout,
  getGoogleAuthUrl,
  getToken,
  setToken,
  removeToken,
  getAuthHeaders,
  getSecureHeaders,
  fetchCsrfToken,
  getCsrfToken
};

export default authService;

